import { atom } from 'jotai';
import { loadable, atomWithStorage } from 'jotai/utils';
import { getMapDefaultConfig } from 'components/map/atoms';
import { atomWithLoadable, mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type {
  AreasQueryResponse,
  DevelopmentAreaQueryResponse,
  SearchResult,
} from 'types/api-responses';
import type {
  CategoriesResponse,
  DevelopmentPaceScenarioResponse,
  GeometryType,
  SaveScenarioParams,
  SlotSearchResponse,
  UnitTypesResponse,
} from './types';

// Re-export for consumers importing from atoms.
export type { CategoriesResponse, UnitTypesResponse } from './types';

// ─────────────────────────────────────
// Toast notification
// ─────────────────────────────────────

interface ToastNotification {
  message: string;
  type: 'error' | 'success';
}

export const toastAtom = atom<ToastNotification | null>(null);

// ─────────────────────────────────────
// Map config — scoped to DPS feature
// ─────────────────────────────────────

export const dpsMapConfigAtom = atomWithStorage(
  'insights-dps-scenario-map-config',
  getMapDefaultConfig({
    wellsV2: {
      checked: false,
      options: {
        surfaceHole: false,
        bottomHole: false,
        stick: false,
        survey: false,
        vertical: false,
        horizontal: false,
        horizontalPermit: false,
        horizontalDuc: false,
        horizontalProducing: false,
        horizontalShutIn: false,
        horizontalExpiredPermit: false,
        horizontalInactive: false,
        horizontalPrePermit: false,
        horizontalApd: false,
        horizontalProposal: false,
        horizontalPlanned: false,
        verticalPermit: false,
        verticalDuc: false,
        verticalProducing: false,
        verticalShutIn: false,
        verticalExpiredPermit: false,
        verticalInactive: false,
        verticalPrePermit: false,
        verticalApd: false,
        verticalProposal: false,
        verticalPlanned: false,
        permit: false,
        duc: false,
        producing: false,
        shutIn: false,
        expiredPermit: false,
        inactive: false,
        prePermit: false,
        apd: false,
        proposal: false,
        planned: false,
      },
    },
    developmentAreas: {
      checked: false,
      options: {
        horizontalUnits: false,
        verticalUnits: false,
        fieldUnits: false,
        pricing: false,
      },
    },
    basinTier: {
      checked: false,
      options: {
        basin: false,
        tier: false,
      },
    },
    titleEntityAssets: {
      checked: false,
      options: {
        surfaceInterest: false,
        workingInterest: false,
        unleased: false,
        royaltyInterest: false,
      },
    },
    scrollZoom: {
      checked: false,
    },
    popups: {
      checked: false,
    },
  })
);

// ─────────────────────────────────────
// Lookup atoms — auto-fetch on mount
// ─────────────────────────────────────

/**
 * Fetches distinct area geometry categories for the "Area Category" dropdown.
 * @return {CategoriesResponse} The categories response
 */
export const [categoriesBaseAtom, categoriesLoadableAtom] = atomWithLoadable(
  async () => {
    const response = await request.get<CategoriesResponse>(
      '/development_pace_scenarios/area_lookups/categories'
    );
    return response as CategoriesResponse;
  }
);

/**
 * Fetches distinct development area unit types for the "Unit Type" dropdown.
 * @return {UnitTypesResponse} The unit types response
 */
export const [unitTypesBaseAtom, unitTypesLoadableAtom] = atomWithLoadable(
  async () => {
    const response = await request.get<UnitTypesResponse>(
      '/development_pace_scenarios/area_lookups/unit_types'
    );
    return response as UnitTypesResponse;
  }
);

// ─────────────────────────────────────
// Typeahead search atoms — reactive to category/type + query
//
// category/type atom: set when user picks from dropdown
// query atom: undefined = not yet focused (no fetch),
//             string = focused/typing (fetch triggers)
// ─────────────────────────────────────

/** The selected area category. */
export const areaCategoryAtom = atom('');

/** The area search query. undefined = no fetch, '' = fetch all, 'text' = filter. */
export const areaQueryAtom = atom<string | undefined>(undefined);

/** Reactive fetch — runs when query becomes a string and category is set. */
const areaSearchBaseAtom = atom(async (get) => {
  const category = get(areaCategoryAtom);
  const query = get(areaQueryAtom);
  if (!category || query === undefined) return null;

  const response = await request.post<
    SearchResult<AreasQueryResponse>,
    unknown
  >(
    '/queries/area_geometries',
    {
      category,
      text: query || undefined,
      page: 1,
      pageSize: 10,
    },
    new AbortController()
  );

  if (response.type !== 'success') {
    throw new Error('Failed to search area geometries');
  }
  return response.data;
});

export const areaSearchLoadableAtom = loadable(areaSearchBaseAtom);

/** The selected unit type. */
export const unitTypeAtom = atom('');

/** The unit search query. undefined = no fetch, '' = fetch all, 'text' = filter. */
export const unitQueryAtom = atom<string | undefined>(undefined);

/** Reactive fetch — runs when query becomes a string and unit type is set. */
const unitSearchBaseAtom = atom(async (get) => {
  const unitType = get(unitTypeAtom);
  const query = get(unitQueryAtom);
  if (!unitType || query === undefined) return null;

  const response = await request.post<
    SearchResult<DevelopmentAreaQueryResponse>,
    unknown
  >(
    '/queries/development_areas',
    {
      unit_type: unitType,
      text: query || undefined,
      page: 1,
      pageSize: 10,
    },
    new AbortController()
  );

  if (response.type !== 'success') {
    throw new Error('Failed to search development areas');
  }
  return response.data;
});

export const unitSearchLoadableAtom = loadable(unitSearchBaseAtom);

// ─────────────────────────────────────
// Slot typeahead — GET /type_curve_scenarios/:id/slots (2.1)
// ─────────────────────────────────────

/** Minimum characters before the slot typeahead queries — matches the server. */
export const SLOT_QUERY_MIN_LENGTH = 2;

interface SlotSearchParams {
  typeCurveScenarioId: number | null;
  query: string | undefined;
}

/** Set by the Property picker: the selected TC scenario + the debounced text. */
export const slotSearchParamsAtom = atom<SlotSearchParams>({
  typeCurveScenarioId: null,
  query: undefined,
});

/**
 * Reactive fetch over the scenario's paceable universe. Idle (null) until a TC
 * scenario is selected and the field has been focused/typed; short input resolves
 * to an empty list without a request, mirroring the endpoint's own gate.
 */
const slotSearchBaseAtom = atom(
  async (get): Promise<SlotSearchResponse | null> => {
    const { typeCurveScenarioId, query } = get(slotSearchParamsAtom);
    if (typeCurveScenarioId === null || query === undefined) return null;
    if (query.trim().length < SLOT_QUERY_MIN_LENGTH) return { slots: [] };

    const response = await request.get<SlotSearchResponse | { error: string }>(
      `/type_curve_scenarios/${typeCurveScenarioId}/slots?q=${encodeURIComponent(
        query
      )}`
    );

    if ('error' in response) {
      throw new Error('Failed to search slots');
    }
    return response;
  }
);

export const slotSearchLoadableAtom = loadable(slotSearchBaseAtom);

// ─────────────────────────────────────
// Geometry fetch — per area entry
// ─────────────────────────────────────

interface FetchGeometryParams {
  entryId: number;
  type: GeometryType;
  configurableId: number;
}

/** Cache of fetched geometries, keyed by area entry ID. */
export const areaGeometriesAtom = atom<Record<number, GeoJSON.Geometry>>({});

/**
 * Fetches geometry for a selected area/unit and stores it in the cache.
 * @param {FetchGeometryParams} params - The entry ID, geometry type, and configurable ID
 * @return {GeoJSON.Geometry} The fetched geometry
 */
export const [fetchGeometryAtom, fetchGeometryLoadableAtom] =
  mutationAtomWithLoadable(
    async (
      _get,
      set,
      params: FetchGeometryParams
    ): Promise<GeoJSON.Geometry> => {
      const path =
        params.type === 'area'
          ? `/area_geometries/${params.configurableId}`
          : `/development_areas/${params.configurableId}`;

      try {
        const response = await request.get<{ geometry: GeoJSON.Geometry }>(
          path
        );
        const geometry = (response as { geometry: GeoJSON.Geometry }).geometry;

        set(areaGeometriesAtom, (prev) => ({
          ...prev,
          [params.entryId]: geometry,
        }));

        return geometry;
      } catch {
        set(toastAtom, {
          message: 'Failed to load geometry',
          type: 'error',
        });
        throw new Error('Failed to load geometry');
      }
    }
  );

// ─────────────────────────────────────
// Save scenario
// ─────────────────────────────────────

/**
 * Saves a development pace scenario via POST (create) or PATCH (update).
 * @param {SaveScenarioParams} params - The mode, optional ID, and payload
 * @return {DevelopmentPaceScenarioResponse} The saved scenario response
 */
export const [saveScenarioAtom, saveScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (
      _get,
      _set,
      params: SaveScenarioParams
    ): Promise<DevelopmentPaceScenarioResponse> => {
      const url =
        params.mode === 'create'
          ? '/development_pace_scenarios'
          : `/development_pace_scenarios/${params.id}`;

      const method = params.mode === 'create' ? request.post : request.patch;

      const response = await method<DevelopmentPaceScenarioResponse, unknown>(
        url,
        params.payload,
        new AbortController()
      );

      if (response.type !== 'success') {
        const data = response.type === 'error' ? response.data : undefined;
        const body = data as { errors?: { message: string }[] } | undefined;
        const message =
          body?.errors?.map((e) => e.message).join(', ') ||
          'Failed to save scenario';
        throw new Error(message);
      }

      return response.data;
    }
  );

// ─────────────────────────────────────
// Clone scenario
// ─────────────────────────────────────

/**
 * Clones a development pace scenario by ID.
 * @param {number} id - The scenario ID to clone
 * @return {DevelopmentPaceScenarioResponse} The cloned scenario response
 */
export const [cloneScenarioAtom, cloneScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (_get, set, id: number): Promise<DevelopmentPaceScenarioResponse> => {
      const response = await request.post<
        DevelopmentPaceScenarioResponse,
        unknown
      >(`/development_pace_scenarios/${id}/clone`, {}, new AbortController());

      if (response.type !== 'success') {
        const data = response.type === 'error' ? response.data : undefined;
        const body = data as { errors?: { message: string }[] } | undefined;
        const message =
          body?.errors?.map((e) => e.message).join(', ') ||
          'Failed to clone scenario';
        set(toastAtom, { message, type: 'error' });
        throw new Error(message);
      }

      return response.data;
    }
  );

// ─────────────────────────────────────
// Fetch scenario (edit mode)
// ─────────────────────────────────────

/**
 * Fetches a development pace scenario by ID for edit mode.
 * @param {number} id - The scenario ID
 * @return {DevelopmentPaceScenarioResponse} The scenario response
 */
export const [fetchScenarioAtom, fetchScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (
      _get,
      _set,
      id: number
    ): Promise<DevelopmentPaceScenarioResponse> => {
      const response = await request.get<DevelopmentPaceScenarioResponse>(
        `/development_pace_scenarios/${id}`
      );

      if (!response || 'error' in response) {
        throw new Error(
          (response as { error: string })?.error ?? 'Failed to load scenario'
        );
      }

      return response;
    }
  );
